import React, { useEffect, useState, useCallback } from "react";
import moment from "moment";
import { ButtonBase } from "@material-ui/core";
import { GetParams, showError } from "../../../Helper";
import { useTranslation } from "react-i18next";
import { DateRangePickerComponent } from "../../../Components/Controls/DateRangePickerComponent/DateRangePickerComponent";
import { Tables, Spinner } from "../../../Components";
import {
  GetMetaCampaignHistoryLog,
  SyncMetaCampaignHistoryLog,
  ExportCampaignHistoryLog,
} from "../../../Services/MetaAdsSync/MetaAdsSync";
import { useTitle } from "../../../Hooks";
import MetaAdsStatCard from "./MetaAdsStatCard/MetaAdsStatCard";
import { MetaCampaignDataTableHeader } from "./MetaCampaignDataTableHeader";

// styles
import "./MetaAdsSync.scss";

const parentTranslationPath = "MetaAdsSync";
function MetaAdsSync() {
  const { t } = useTranslation(parentTranslationPath);
  const propertyCampaignId = +GetParams("propertyCampaignId");
  useTitle(t(`meta-ads-sync`));

  const [syncHistoryData, setSyncHistoryData] = useState({
    pagedResult: { result: [], totalCount: 0 },
    totalLeadsSummary: 0,
    totalSpendSummary: 0,
    totalBudgetSummary: 0,
    totalCostPerLeadSummary: 0,
    totalAdsets: 0,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [filter, setFilter] = useState({
    pageIndex: 0,
    pageSize: 25,
    syncDateFrom: null,
    syncDateTo: null,
    propertyCampaignId,
  });
  console.log("🚀 ~ MetaAdsSync ~ filter:", filter.pageIndex);

  function FormatUpTo3Decimals(num) {
    if (typeof num !== "number") return num;
    if (Number.isInteger(num)) return num;
    return parseFloat(num.toFixed(3));
  }

  const handleExportHistory = async () => {
    try {
      const body = {
        filter: {
          propertyCampaignId,
          syncDateFrom: filter.syncDateFrom,
          syncDateTo: filter.syncDateTo,
          pageIndex: filter.pageIndex + 1,
          pageSize: filter.pageSize,
        },
        exportType: 1,
      };
      await ExportCampaignHistoryLog(body);
    } catch (error) {
      showError(t("error-fetching-data"));
    }
  };

  const handleManualSync = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await SyncMetaCampaignHistoryLog({
        propertyCampaignId,
      });
      setSyncHistoryData(response);
    } catch (error) {
      let msg =
        error.data && error.data.Message
          ? error.data.Message
          : t("error-fetching-data");
      if (msg.includes(":")) {
        msg = msg.split(":").pop().trim();
      }
      showError(msg);
      getHistoryLog();
    } finally {
      setIsLoading(false);
    }
  }, [propertyCampaignId]);

  const getHistoryLog = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await GetMetaCampaignHistoryLog({
        ...filter,
        pageIndex: filter.pageIndex + 1,
      });
      setSyncHistoryData(response);
    } catch (error) {
      showError(t(`error-fetching-data`));
      setSyncHistoryData({
        pagedResult: { result: [], totalCount: 0 },
        totalLeadsSummary: 0,
        totalSpendSummary: 0,
        totalBudgetSummary: 0,
        totalCostPerLeadSummary: 0,
        totalAdsets: 0,
      });
    } finally {
      setIsLoading(false);
    }
  }, [propertyCampaignId, filter]);

  const stats = {
    totalLeads: syncHistoryData.totalLeadsSummary,
    totalSpent: syncHistoryData.totalSpendSummary,
    totalDailyBudget: syncHistoryData.totalBudgetSummary,
    costPerLead: syncHistoryData.totalCostPerLeadSummary,
    totalCount: syncHistoryData.totalAdsets,
    syncStatus: syncHistoryData.pagedResult?.result?.[0]?.syncStatus || "",
    syncDate: syncHistoryData.pagedResult?.result?.[0]?.syncDate || "",
  };

  useEffect(() => {
    if (propertyCampaignId) {
      getHistoryLog();
    }
  }, [propertyCampaignId, filter]);

  return (
    <div className="meta-ads-sync-page">
      <Spinner isActive={isLoading} />
      <div
        className="meta-ads-stats-row"
        style={{ display: "flex", flexWrap: "wrap", marginBottom: 32 }}
      >
        <MetaAdsStatCard
          title={t(`last-sync-status`)}
          value={stats.syncStatus || ""}
          subLabel={
            stats.syncDate ? new Date(stats.syncDate).toLocaleString() : "-"
          }
          valueColor={stats.syncStatus === "Success" ? "#1db954" : "#e53935"}
        />
        <MetaAdsStatCard
          title={t(`total-adset`)}
          value={stats.totalCount || 0}
          valueColor="#222"
          subLabel={
            syncHistoryData.pagedResult?.result?.length > 0
              ? syncHistoryData.pagedResult.result.every(
                  (item) => item.syncStatus === "Success"
                )
                ? t(`all-successful`)
                : t(`some-failed`)
              : "-"
          }
          subLabelColor={
            syncHistoryData.pagedResult?.result?.length > 0
              ? syncHistoryData.pagedResult.result.every(
                  (item) => item.syncStatus === "Success"
                )
                ? "#1db954"
                : "#e53935"
              : "#222"
          }
        />
        <MetaAdsStatCard
          title={t(`total-daily-budget`)}
          value={`AED ${FormatUpTo3Decimals(stats.totalDailyBudget || 0)}`}
          subLabel={t(`combined-budgets`)}
        />
        <MetaAdsStatCard
          title={t(`total-spend`)}
          value={`AED ${FormatUpTo3Decimals(stats.totalSpent || 0)}`}
          subLabel={t(`combined-budgets`)}
        />
        <MetaAdsStatCard
          title={t(`total-leads`)}
          value={stats.totalLeads || 0}
          subLabel={t(`combined-leads-number`)}
        />
        <MetaAdsStatCard
          title={t(`cost-per-lead`)}
          value={FormatUpTo3Decimals(stats.costPerLead || 0)}
          subLabel={t(`average-cost`)}
        />
      </div>
      <div className="meta-ads-sync-content">
        <div className="above-table-content">
          <div className="meta-ads-sync-actions">
            <ButtonBase
              className="MuiButtonBase-root btns theme-solid"
              onClick={() => handleManualSync()}
              disabled={isLoading}
            >
              <span className="mdi mdi-sync"></span>
              {t(`manual-sync`)}
            </ButtonBase>
            <ButtonBase
              className="MuiButtonBase-root btns theme-outline"
              onClick={handleExportHistory}
              disabled={isLoading}
            >
              <span className="mdi mdi-download"></span>
              {t(`export-history`)}
            </ButtonBase>
          </div>
          <div className="meta-ads-sync-filters">
            <DateRangePickerComponent
              emptyLabel={t(`all`)}
              wrapperClasses="w-100 styled-data-picker "
              ranges={[
                {
                  startDate: filter.syncDateFrom,
                  endDate: moment(filter.syncDateTo),
                  key: "selection",
                },
              ]}
              onClearClicked={() =>
                setFilter((prev) => ({
                  ...prev,
                  syncDateFrom: null,
                  syncDateTo: null,
                }))
              }
              onDateChanged={(e) => {
                setFilter((prev) => ({
                  ...prev,
                  syncDateFrom: new Date(
                    moment(e.selection.startDate).startOf("day")
                  ),
                  syncDateTo: new Date(
                    moment(e.selection.endDate).endOf("day")
                  ),
                }));
              }}
            />
          </div>
        </div>
        <Tables
          data={syncHistoryData.pagedResult?.result || []}
          headerData={MetaCampaignDataTableHeader}
          defaultActions={[]}
          parentTranslationPath={parentTranslationPath}
          itemsPerPage={filter.pageSize}
          activePage={filter.pageIndex}
          totalItems={syncHistoryData.pagedResult?.totalCount || 0}
          onPageIndexChanged={(pageIndex) => {
            setFilter((prev) => ({ ...prev, pageIndex }));
          }}
          onPageSizeChanged={(pageSize) => {
            setFilter((prev) => ({ ...prev, pageSize, pageIndex: 0 }));
          }}
        />
      </div>
    </div>
  );
}

export default MetaAdsSync;
